import { notifications } from "@mantine/notifications";
import { safeApiCall } from "api/common.service";
import { getSupplierOrderById } from "api/supplier.service";
import {
  MantineNotificationColor,
  MantineNotificationPosition,
  MSG_DEFAULT_DOWNLOAD_TITLE,
  MSG_DEFAULT_FORM_DATA_LOAD_ERROR,
} from "common/constants";
import { pickFields, toCsv } from "common/csv";
import { formatDate } from "common/utils";
import PreviewHeader from "components/header/PreviewHeader";
import React, { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import type {
  SupplierOrderDataEntryForm,
  SupplierOrderEntry,
} from "types/model.types";

interface AdminSupplierOrderPreviewProps {
  recordId: number | null;
  previewType?: "altCode" | "noAltCode" | "summary" | "costing" | "prices";
  onClose?: () => void;
}

const AdminSupplierOrderPreview: React.FC<AdminSupplierOrderPreviewProps> = ({
  previewType = "altCode",
  recordId,
  onClose,
}) => {
  // 1. Receive the trigger from MainPrintPage
  const { csvRequestId } = useOutletContext<{ csvRequestId: number }>();

  const apiFetchRef = React.useRef<boolean>(false);
  const [initialData, setInitialData] =
    useState<SupplierOrderDataEntryForm | null>(null);

  // 2. React to the trigger
  useEffect(() => {
    if (!csvRequestId) return; // ignore initial mount

    // Generate CSV from the data inside THIS print page
    const summaryRow = {
      Date: formatDate(initialData?.summary.createdOn ?? ""),
      Supplier: initialData?.summary.supplierName,
    };

    const CSV_FIELDS: (keyof SupplierOrderEntry)[] =
      previewType === "altCode"
        ? [
            "altCode",
            "productId",
            "productCode",
            "productDescription",
            "actual", // ordered
            "quantity",
          ]
        : previewType === "summary"
          ? [
              "productId",
              "productCode",
              "barcode",
              "productDescription",
              "bestBeforeDate",
              "supplierBarcode",
              "quantity",
            ]
          : [
              "altCode",
              "productId",
              "productCode",
              "productDescription",
              "actual", // ordered
              "quantity",
            ];

    const detailRows = pickFields(initialData?.entries ?? [], CSV_FIELDS);
    const summaryCsv = toCsv([summaryRow]);
    const csv = toCsv(detailRows);

    const blob = new Blob([`${summaryCsv}\n\n${csv}`], {
      type: "text/csv;charset=utf-8;",
    });
    //const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.download = `supplier-order-${previewType}.csv`;
    link.click();

    URL.revokeObjectURL(url);
  }, [csvRequestId]);

  useEffect(() => {
    if (!recordId || recordId <= 0) return; // only fetch if we have a recordId

    if (apiFetchRef.current) return;
    apiFetchRef.current = true;

    const fetchInitialData = async () => {
      try {
        const response = await safeApiCall(
          getSupplierOrderById(recordId, true),
        );
        apiFetchRef.current = false;

        if (response) {
          setInitialData(response);
        }
      } catch (error) {
        apiFetchRef.current = false;
        notifications.show({
          title: MSG_DEFAULT_DOWNLOAD_TITLE,
          message: MSG_DEFAULT_FORM_DATA_LOAD_ERROR,
          position: MantineNotificationPosition.TopCenter,
          color: MantineNotificationColor.Error,
        });
      }
    };
    fetchInitialData();
  }, [recordId]);

  return (
    <>
      <PreviewHeader hideAddress={true} />
      <div className="sub-title">
        Supplier Order Number: {initialData?.summary.id}
      </div>
      <div className="spacer padTop padBottom" />

      <table className="print-table">
        <tbody>
          <tr>
            <td className="leftCol">
              <div>
                <span className="rcl">Date:</span>
                <span className="rcv">
                  {formatDate(
                    initialData?.summary.createdOn ?? new Date().toDateString(),
                  )}
                </span>
              </div>
            </td>

            <td className="rightCol">
              <div>
                <span className="rcl">Supplier:</span>
                <span className="rcv">{initialData?.summary.supplierName}</span>
              </div>
            </td>
          </tr>
        </tbody>
      </table>

      <div className="spacer padTop padBottom" />

      {/* Render logic for orderDetails array here */}
      <table className="print-table details-table">
        <thead>
          <tr>
            {previewType === "altCode" && <th className="nowrap">Alt Code</th>}
            <th className="nowrap">Product</th>
            {previewType === "summary" && <th>Barcode</th>}
            <th className="description">Description</th>
            {previewType === "summary" && (
              <>
                <th>Best before</th>
                <th>Shipper Barcode</th>
              </>
            )}
            {previewType !== "summary" && (
              <>
                <th className="money">Ordered</th>
              </>
            )}
            <th className="money">Quantity</th>
          </tr>
        </thead>

        <tbody>
          {initialData?.entries.map((item) => (
            <tr key={item.id}>
              {previewType === "altCode" && (
                <td className="nowrap">{item.altCode}</td>
              )}
              <td>{`${item.productId}${item.productCode}`}</td>
              {previewType === "summary" && <td>{item.barcode}</td>}
              <td className="description">{item.productDescription}</td>
              {previewType === "summary" && (
                <>
                  <td>{item.bestBeforeDate}</td>
                  <td>{item.supplierBarcode}</td>
                </>
              )}
              {previewType !== "summary" && (
                <>
                  <td className="money">{item.quantity}</td>
                </>
              )}
              <td className="money">{item.actual}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
};

export default AdminSupplierOrderPreview;
